import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm , useFieldArray } from 'react-hook-form';
import { Layout } from '../../../../components';
import AppContext from '../../../../contexts/AppContext';
import { handleApi, formatCurrency, formatToTwoDecimals, formatMediumDate, capitalize, AudioSpinner,useToast } from '../../../../utils/utils';



// New component for the filterable product input
const ProductSearchInput = ({ products, onSelectProduct, selectedProduct }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  // Add a safeguard to ensure 'products' is a valid array before filtering
  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    if (!product || typeof product.label !== 'string') {
      return false; // Skip any non-valid product objects
    }
    return product.label.toLowerCase().includes(searchTerm.toLowerCase());
  });
  

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  // FINAL FIX: Add a check to ensure selectedProduct is a valid object before trying to access its properties.
  useEffect(() => {
    if (selectedProduct && typeof selectedProduct.product_name === 'string') {
      setSearchTerm(selectedProduct.product_name);
    } else {
      setSearchTerm('');
    }
  }, [selectedProduct]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <input
        type="text"
        className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2"
        placeholder="Search product..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
      />
      {showDropdown && (
        <ul className="absolute z-10 w-full mt-1 bg-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <li
                key={product.value}
                className="p-2 text-white hover:bg-cyan-200 hover:text-gray-800 cursor-pointer"
                onClick={() => {
                  onSelectProduct(product);
                  setSearchTerm(product.label);
                  setShowDropdown(false);
                }}
              >
                {product.label}
              </li>
            ))
          ) : (
            <li className="p-2 text-gray-400">No products found.</li>
          )}
        </ul>
      )}
    </div>
  );
};


const NewQuotation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { initialQuotation } = location.state || {};
  const initialFormState = {
    date: new Date().toISOString().split('T')[0],
    partner_id: '',
    state: 'draft',
    description: '',
    transactionLines: [{
      product_id: null,
      product_name: '',
      description: '',
      unit_price: 0,
      discount_percentage: 0,
      quantity: 1,
      total: 0,
    }],
  };
  const [quotation, setQuotation] = useState(initialFormState);
  const [transactionLines, setTransactionLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [formattedProducts, setFormattedProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formattedCustomers, setFormattedCustomers] = useState([]);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null); //state to hold customer details
  const { userData, currentColor, actionCount,setActionCount} = useContext(AppContext);
  console.log('initialQuotation',initialQuotation)
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false); // State for the reset confirmation modal
  const [focusedIndex, setFocusedIndex] = useState(null);
  const quotationRef = useRef(null); // Ref to the quotation content for PDF generation
  const quantityRefs = useRef([]);    // a ref for each quantity input field to manage focus

  const { register, control, watch, handleSubmit, reset, setValue, getValues } = useForm({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      partner_id: '',
      state: 'draft',
      name: '',
      transactionLines: []
    }
  });
  
  

  const { fields, append, remove } = useFieldArray({// Use useFieldArray to manage the transactionLines array
    control, 
    name: "transactionLines",
  });


  const watchedPartnerId = watch('partner_id'); // Watch for changes on the 'partner_id' field
  const watchedDate = watch('date');
  const watchedDescription = watch('name');
  
  // The transaction lines section will only be shown if both fields have a value.
  const showTransactionLines = watchedDate && watchedDescription;

  // New, reusable function to fetch data. It now accepts `showToast` as a parameter.
  const fetchData = async (apiEndpoint, showToast) => {
    const apiMethod = 'GET';
    const cleanedData = {};
    const parameters = {};

    try {
      const response = await handleApi(apiEndpoint, cleanedData, userData, apiMethod, parameters);
      if (response.error) {
        console.error(`Error fetching data from ${apiEndpoint}: ${response.errorMessage}`);
        showToast(`Error: ${response.errorMessage}`, 'error'); // Use showToast here
        return null; // Return null on error
      }
      const { status, data } = response;
      if (status === 200) {
        return data; // Return the data on success
      } else {
        showToast(`Error ${status}: ${response.statusText}`, 'error'); // Use showToast here
        return null;
      }
    } catch (error) {
      showToast(error.message || `An unexpected error occurred while fetching ${apiEndpoint}.`, 'error'); // Use showToast here
      return null;
    }
  };
 
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch customers
      const customersData = await fetchData('customers', showToast);
      if (customersData) {
        setCustomers(customersData);
        setFormattedCustomers(customersData.map(c => ({
          value: c.id,
          label: c.name
        })));
      }

      // Fetch products
      const productsData = await fetchData('products', showToast);
      console.log('productsData',productsData)
      if (productsData) {
        setProducts(productsData);
        // FIX: Filter out null/undefined values before mapping
        setFormattedProducts(productsData.filter(p => p).map(p => ({
          value: p.id,
          label: p.name || '',
          unit_price: p.prices?.sell_price_new || 0,
          sell_tax_info: p.sell_tax_info || { percentage: '0.00' }  //  Pass the tax info along
        })));
      }
    };
    loadInitialData();
  }, []);


    // 💡 NEW LOGIC: This function is called when the description field loses focus.
  const handleDescriptionBlur = () => {
    // Get the current value of the description field.
    const descriptionValue = getValues('name');
    
    // Check if the description is not empty AND there are no lines yet.
    // This prevents adding a duplicate line if the user clicks away and back.
    if (descriptionValue && fields.length === 0) {
        append({
          product_id: null,
          product_name: '',
          description: '',
          unit_price: 0,
          discount_percentage: 0,
          quantity: 1,
        });
    }
};


  /* useEffect(() => {    // New useEffect to handle adding an initial blank line
    if (showTransactionLines && transactionLines.length === 0) {addTransactionLine();}
  }, [showTransactionLines, transactionLines.length]); */

  // Use a useEffect hook to update the customer details whenever the watchedPartnerId changes
  useEffect(() => {
    if (watchedPartnerId) {
      const selectedCustomer = customers.find(c => c.id === parseInt(watchedPartnerId));
      setSelectedCustomerDetails(selectedCustomer || null);
    } else {setSelectedCustomerDetails(null); }
  }, [watchedPartnerId, customers]);

  // Handle input change and add new line immediately
  const handleInputChange = (index, field, value) => {
    const parsedValue = field === 'quantity' || field === 'unit_price' || field === 'discount_percentage'
      ? parseFloat(value) : value;

    setValue(`transactionLines.${index}.${field}`, parsedValue);

    const latestLine = getValues(`transactionLines.${index}`);
    const lineTotal = (latestLine.unit_price || 0) * (latestLine.quantity || 0) * (1 - (latestLine.discount_percentage || 0) / 100);

    // 💡 NEW LOGIC: If this is the last line and its total becomes 0, remove the extra blank line
    if (lineTotal <= 0 && index === fields.length - 2) {
      remove(fields.length - 1);
    }
    // 💡 ORIGINAL LOGIC: If this is the last line and has a total, add a new blank line
    else if (lineTotal > 0 && index === fields.length - 1) {
      append({
        product_id: null,
        product_name: '',
        description: '',
        unit_price: 0,
        discount_percentage: 0,
        quantity: 1,
        tax_percentage: 0, // 💡 NEW: Add a default tax percentage
      });
    }
  };


  // Handle product selection from the search input
  const handleProductSelection = (index, product) => {
    // 3. Use setValue from react-hook-form to update fields
    setValue(`transactionLines.${index}.product_name`, product.label);
    setValue(`transactionLines.${index}.product_id`, product.value);
    setValue(`transactionLines.${index}.unit_price`, product.unit_price);
    setValue(`transactionLines.${index}.description`, product.label);
  };

  
  const handleAction = (action) => {
    if (action === 'download') {
      showToast(`Simulated action: ${action}`, 'success');
    } else if (action === 'print') {window.print();
    } else {showToast(`Simulated action: ${action}`, 'success');}
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    reset(initialFormState); // Reset form fields
    setQuotation(initialFormState); // Reset state for other components
    setSelectedCustomerDetails(null); // Clear selected customer details
    setShowResetModal(false); // Close the modal
    showToast('Quotation form has been reset!', 'info');
  };

  const cancelReset = () => {
    setShowResetModal(false);
  };

// New function to handle saving the form data
  const handleSave = async (data) => {
    // Step 1: Filter out any lines that are completely empty based on key fields
    const filteredLines = data.transactionLines.filter(line => 
      (line.description || line.product_id) && line.quantity > 0
    );
    
    // Step 2: Map over the filtered lines to create a new, clean payload.
    // This is where we explicitly create a new object without the 'product_name' key.
    const cleanedLines = filteredLines.map(line => ({
      product_id: line.product_id,
      description: line.description,
      unit_price: line.unit_price,
      discount_percentage: line.discount_percentage,
      quantity: line.quantity,
      tax_percentage: line.tax_percentage
    }));
    
    const dataToSave = { ...data, transactionLines: cleanedLines };
    const apiEndpoint = 'sales/orders';
    
    try {
      setIsLoading(true);
      // Make API call to save the data
      const response = await handleApi(apiEndpoint, dataToSave, userData, 'POST');
      if (response.status === 200) {
        showToast(response.data.message || 'Quotation saved successfully!', 'success');
      } else {
        showToast('Failed to save quotation.', 'error');
      }
    } catch (error) {
      showToast('An error occurred while saving the quotation.', 'error');
      console.error('Save error:', error);
    } finally {
      setIsLoading(false);
    }
  };



  const TransactionLineHeader = () => {
    return (
      <div className="grid grid-cols-6 gap-2 p-2 border-b rounded-lg bg-gray-100 text-gray-800 font-semibold text-xs md:text-xs">
        <div className="col-span-2 text-left">Product Description</div>
        <div className="col-span-1 text-center">Price</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-1 text-center">Disc %</div>
        <div className="col-span-1 text-right">Total</div>
      </div>
    );
  };
  // Get the current list of transaction lines to calculate totals
  // Get the current list of transaction lines to calculate totals
  const currentLines = watch("transactionLines") || [];
  console.log('currentLines',currentLines)
  // Calculate summary totals from the current lines
  const subtotal = currentLines.reduce((acc, line) => acc + ((line.unit_price || 0) * (line.quantity || 0)), 0);
  const discountAmount = currentLines.reduce((acc, line) => {
      const lineTotal = (line.unit_price || 0) * (line.quantity || 0);
      return acc + (lineTotal * ((line.discount_percentage || 0) / 100) || 0);
  }, 0);
  const subtotalAfterDiscount = subtotal - discountAmount;
  
  // 💡 NEW LOGIC: Calculate tax by iterating through each line's specific tax info
  const taxAmount = currentLines.reduce((acc, line) => {
      const lineTotal = (line.unit_price || 0) * (line.quantity || 0) * (1 - (line.discount_percentage || 0) / 100);
      const taxRate = parseFloat(line.tax_percentage || 0) / 100;
      console.log('tax rate sir ',taxRate)
      return acc + (lineTotal * taxRate);
  }, 0);

  console.log('tax amount sir ',taxAmount);

  const grandTotal = subtotalAfterDiscount + taxAmount;
  
  const formattedSubtotal = formatCurrency(subtotal);
  const formattedDiscount = formatCurrency(discountAmount);
  const formattedSubtotalAfterDiscount = formatCurrency(subtotalAfterDiscount);
  const formattedTax = formatCurrency(taxAmount);
  const formattedGrandTotal = formatCurrency(grandTotal);

  // Add a check to see if there is at least one transaction line with a total greater than 0
  const showSummary = currentLines.some(line => ((line.unit_price || 0) * (line.quantity || 0) * (1 - (line.discount_percentage || 0) / 100)) > 0);
  
  return (
    <Layout>
    <div className="min-h-screen text-white p-2 sm:p-4">
      {toastMessage.show && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
                        ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toastMessage.message}
        </div>
      )}

      <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center text-xl">
            NEW QUOTATION
          </h1>
          <div className="flex gap-2">
            
            
            
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

      <div className="bg-gray-600 rounded-3xl shadow-lg p-2 sm:p-4 mx-auto max-w-[80%]">
        

        {isLoading ? (
          <div className="flex flex-col items-center py-6">
            <AudioSpinner />
            <p className="mt-4 text-gray-400 text-base">Generating PDF. Please wait...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="bg-gray-500 px-4 py-1 rounded-xl text-cyan-200">
                    <p className="font-semibold text-white text-center mb-2 uppercase text-base">Customer Info</p>
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex flex-col">
                        <select
                          className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 text-base"
                          {...register("partner_id")}
                        >
                          <option value="" className="text-gray-800 text-base">
                            Select a customer...
                          </option>
                          {formattedCustomers.map((option) => (
                            // ✅ FIX: Removed the unnecessary class from option, letting the select's text-base apply
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedCustomerDetails && (
                        <>
                          <div className="flex justify-between items-center">
                            <label className="font-medium text-cyan-200">Telephone</label>
                            <span className="text-white font-medium text-right">{selectedCustomerDetails?.phone_number || 'NA'}</span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <label className="font-medium text-cyan-200">Contact Person</label>
                            <span className="text-white font-medium text-right">{selectedCustomerDetails?.contact_person || 'NA'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <label className="font-medium text-cyan-200">Email Address</label>
                            <span className="text-white font-medium text-right">{selectedCustomerDetails?.email || 'NA'}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-500 px-4 py-1 rounded-xl text-cyan-200">
                    <p className="font-semibold text-white text-center mb-2 uppercase text-base">Quotation Info</p>
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex flex-col">
                        <label className="font-medium text-cyan-200 mb-1">Description</label>
                        <textarea
                          className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 min-h-[25px] resize-y"
                          {...register("name")}
                          placeholder="Enter quotation description..."
                          onBlur={handleDescriptionBlur}
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <label className="font-medium text-cyan-200">Date</label>
                        <input
                          type="date"
                          className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1"
                          {...register("date")}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              {/* Conditionally render the TransactionLines section */}
              {showTransactionLines && (
                <>
                  <div className="bg-gray-500 rounded-lg">
                    {/* Updated to a 10-column grid for better control over sizing */}
                    <div className="grid grid-cols-10 gap-2 p-2 border-b rounded-lg bg-gray-100 text-gray-800 font-semibold text-sm md:text-base">
                      <div className="col-span-4 text-left">Product</div>
                      <div className="col-span-2 text-center">Price</div>
                      <div className="col-span-1 text-center">Qty</div>
                      <div className="col-span-1 text-center">Disc %</div>
                      <div className="col-span-2 text-right">Total</div>
                    </div>
                    <div className="px-1">
                      {/* Map over the fields array from useFieldArray */}
                      {fields.length > 0 ? (
                        <>
                          {fields.map((field, index) => {
                            const transactionLine = watch(`transactionLines.${index}`);
                            const isProductSelected = transactionLine.product_id;
                            
                            // Check if this is the last item in the list
                            const isLastLine = index === fields.length - 1;

                            return (
                              <div key={field.id} className="grid grid-cols-10 items-center px-3 py-1 rounded-lg hover:bg-gray-400 transition-colors duration-200">
                                {/* Product / Description Column */}
                                <div className="col-span-4 relative text-sm">
                                  <input
                                    type="text"
                                    className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2"
                                    placeholder="Description..."
                                    {...register(`transactionLines.${index}.description`)}
                                    onFocus={() => setFocusedIndex(index)}
                                    onBlur={() => setTimeout(() => setFocusedIndex(null), 200)} // Delay to allow click
                                  />
                                  {/* Show dropdown only when the field is focused */}
                                  {focusedIndex === index && (
                                    <ul className="absolute z-10 w-full mt-1 bg-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                      {/* Filter products based on the current input value */}
                                      {formattedProducts
                                        .filter(p => p.label.toLowerCase().includes(watch(`transactionLines.${index}.description`)?.toLowerCase() || ''))
                                        .map((product) => (
                                          <li
                                            key={product.value}
                                            className="p-2 text-white hover:bg-cyan-200 hover:text-gray-800 cursor-pointer text-sm"
                                            onMouseDown={() => {
                                              const currentLines = getValues('transactionLines');
                                              const existingLineIndex = currentLines.findIndex((line, lineIndex) => 
                                                lineIndex !== index && line.product_id === product.value
                                              );
                                              
                                              if (existingLineIndex !== -1) {
                                                // We no longer remove the new line. We just increment the quantity and set focus.
                                                const existingQuantity = currentLines[existingLineIndex].quantity || 0;
                                                setValue(`transactionLines.${existingLineIndex}.quantity`, existingQuantity + 1);
                                                
                                                // Use a short delay to ensure React has re-rendered before focusing
                                                setTimeout(() => {
                                                  quantityRefs.current[existingLineIndex]?.focus();
                                                }, 50);
                                                
                                              } else {
                                                // If it doesn't exist, set the values for the current line
                                                setValue(`transactionLines.${index}.product_id`, product.value);
                                                setValue(`transactionLines.${index}.description`, product.label);
                                                // 💡 NEW: Set the unit_price directly from the product data
                                                setValue(`transactionLines.${index}.unit_price`, product.unit_price);
                                                // 💡 NEW: Set the tax percentage for this specific line
                                                setValue(`transactionLines.${index}.tax_percentage`, parseFloat(product.sell_tax_info.percentage));
                                                
                                                // Append a new line only if this is the last line and has a total
                                                if (isLastLine && product.unit_price > 0) {
                                                  append({
                                                    product_id: null,
                                                    product_name: '',
                                                    description: '',
                                                    unit_price: 0,
                                                    discount_percentage: 0,
                                                    quantity: 1,
                                                    tax_percentage: 0,
                                                  });
                                                }
                                              }
                                              setFocusedIndex(null); // Close the dropdown
                                            }}
                                          >
                                            {product.label}
                                          </li>
                                        ))
                                      }
                                      {/* Show "no products found" message if no matches */}
                                      {formattedProducts.filter(p => p.label.toLowerCase().includes(watch(`transactionLines.${index}.description`)?.toLowerCase() || '')).length === 0 && (
                                        <li className="p-2 text-gray-400 text-sm">No products found.</li>
                                      )}
                                    </ul>
                                  )}
                                  
                                </div>
                                {/* Price Column */}
                                <div className="col-span-2 text-center text-sm">
                                  <input
                                    type="number"
                                    className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2 text-center"
                                    {...register(`transactionLines.${index}.unit_price`, { valueAsNumber: true })}
                                    onChange={(e) => handleInputChange(index, 'unit_price', parseFloat(e.target.value))}
                                  />
                                </div>
                                {/* Quantity Column */}
                                <div className="col-span-1 text-center text-sm">
                                  <input
                                    type="number"
                                    className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2 text-center"
                                    {...register(`transactionLines.${index}.quantity`)}
                                    onChange={(e) => handleInputChange(index, 'quantity', e.target.value)}
                                    value={transactionLine.quantity || ''}
                                    ref={el => quantityRefs.current[index] = el}
                                  />
                                </div>
                                {/* Discount Column */}
                                <div className="col-span-1 text-center text-sm">
                                  <input
                                    type="number"
                                    className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2 text-center"
                                    {...register(`transactionLines.${index}.discount_percentage`, { valueAsNumber: true })}
                                    onChange={(e) => handleInputChange(index, 'discount_percentage', parseFloat(e.target.value))}
                                  />
                                </div>
                                {/* Total Column */}
                                <div className="col-span-2 flex items-center justify-between text-white text-sm">
                                  <span className="flex-grow text-right">
                                    {formatCurrency(watch(`transactionLines.${index}.unit_price`) * watch(`transactionLines.${index}.quantity`) * (1 - watch(`transactionLines.${index}.discount_percentage`) / 100))}
                                  </span>
                                  <button
                                    onClick={() => remove(index)}
                                    className="ml-2 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <p className="p-6 text-center text-gray-400 text-sm">No transactions found.</p>
                      )}
                    </div>
                  </div>
                </>
              )}


              {/* Conditionally render the Invoice Summary section based on the showSummary variable */}
              {showSummary && (
                <div className="bg-gray-500 px-4 py-2 rounded-xl shadow-lg">
                  <div className="font-semibold text-lg sm:text-xl text-white mb-2">Invoice Summary</div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-center py-1 border-b border-cyan-200">
                      <div className="text-sm text-gray-200">Subtotal:</div>
                      <div className="text-sm font-semibold text-white">{formattedSubtotal}</div>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-cyan-200">
                      <div className="text-sm text-gray-200">Discount:</div>
                      <div className="text-sm font-semibold text-red-400">- {formattedDiscount}</div>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-cyan-200">
                      <div className="text-sm text-gray-200">Subtotal After Discount:</div>
                      <div className="text-sm font-semibold text-white">{formattedSubtotalAfterDiscount}</div>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-cyan-200">
                      <div className="text-sm text-gray-200">Tax:</div>
                      <div className="text-sm font-semibold text-white">{formattedTax}</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-1 font-semibold text-lg text-gray-100 border-cyan-200 ">
                    <div className="text-base">Grand Total:</div>
                    <div className="text-base">{formattedGrandTotal}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-between mt-6">
        <button
              type="button"
              // Add the onClick handler to call handleReset
              onClick={handleReset}
              className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
              >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit(handleSave)}
              className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
      </div>
     
    </div>
    {showResetModal && (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50">
        <div className="text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300 px-4 py-2 rounded-xl shadow-lg  max-w-sm w-full mx-4">
          <h3 className="text-xl font-bold mb-2">Confirm Reset</h3>
          <p className="mb-4 text-base">Are you sure you want to reset the form? All entered data will be lost.</p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={cancelReset}
              className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-gray-300 hover:bg-gray-400 transition-all duration-300"
            >
              No, Keep Editing
            </button>
            <button
              onClick={confirmReset}
              className="px-4 py-2 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-all duration-300"
            >
              Yes, Reset
            </button>
          </div>
        </div>
      </div>
    )}
    </Layout>
  );
};

export default NewQuotation;


